/**
 * Custom Translation Hook for SmartDry POS
 * 
 * A lightweight, type-safe translation system without external dependencies.
 * Automatically handles RTL languages and parameter interpolation.
 */

import React, { createContext, useState, useContext, useCallback, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { translations, SupportedLanguage, TranslationKey } from './translations';

// Define Language type alias for clarity
export type Language = SupportedLanguage;

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: TranslationKey, params?: { [key: string]: string | number }) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Inner Language Provider Component (must be inside Router)
 * 
 * Language state is independent for POS and Dashboard:
 * - POS routes (/pos/*) use 'smartdry_language_pos'
 * - Dashboard routes (/dashboard/*, /customers/*, etc.) use 'smartdry_language_dashboard'
 * - Other routes default to 'smartdry_language' for backward compatibility
 */
const LanguageProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  
  const getLanguageKeyForPath = useCallback((path: string) => {
    if (path.startsWith('/pos')) {
      return 'smartdry_language_pos';
    } else if (path.startsWith('/dashboard') || path.startsWith('/customers') || 
                path.startsWith('/items') || path.startsWith('/settings') ||
                path.startsWith('/accounts') || path.startsWith('/hr') ||
                path.startsWith('/reports')) {
      return 'smartdry_language_dashboard';
    }
    // Default/fallback key for other routes
    return 'smartdry_language';
  }, []);

  // Load saved language from localStorage based on context, default to 'en'
  const [language, setLanguageState] = useState<Language>(() => {
    const key = getLanguageKeyForPath(location.pathname);
    let saved = localStorage.getItem(key);
    
    // Migration: If no context-specific language exists, check for legacy key
    if (!saved) {
      const legacyLang = localStorage.getItem('smartdry_language');
      if (legacyLang) {
        // Migrate legacy language to both POS and Dashboard keys
        localStorage.setItem('smartdry_language_pos', legacyLang);
        localStorage.setItem('smartdry_language_dashboard', legacyLang);
        saved = legacyLang;
      }
    }
    
    return (saved as Language) || 'en';
  });

  // Update language when route changes (switch between POS and Dashboard)
  useLayoutEffect(() => {
    const key = getLanguageKeyForPath(location.pathname);
    const saved = localStorage.getItem(key);
    if (saved && saved !== language) {
      setLanguageState(saved as Language);
    }
  }, [location.pathname, getLanguageKeyForPath, language]);

  // Wrapper to save language to localStorage when changed
  const setLanguage = useCallback((newLang: Language) => {
    setLanguageState(newLang);
    const key = getLanguageKeyForPath(location.pathname);
    localStorage.setItem(key, newLang);
  }, [getLanguageKeyForPath, location.pathname]);

  // Check if current language is RTL
  const isRTL = ['ar', 'ur'].includes(language);

  /**
   * Automatically set document direction based on language
   * Uses useLayoutEffect to prevent flash of incorrect direction
   */
  useLayoutEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  /**
   * Translation function with parameter interpolation
   * 
   * @param key - Translation key (e.g., 'pos.sidebar.home')
   * @param params - Optional parameters for interpolation (e.g., { count: 5 })
   * @returns Translated string with parameters replaced
   * 
   * Example:
   * t('cart.itemCount', { count: 3 }) → "3 items"
   */
  const t = useCallback(
    (key: TranslationKey, params: { [key: string]: string | number } = {}): string => {
      // Get translation from current language, fallback to English, then show key
      const keys = key.split('.');
      let text: any = translations[language];
      
      // Navigate through nested object
      for (const k of keys) {
        text = text?.[k];
      }
      
      // Fallback to English if not found
      if (typeof text !== 'string') {
        let fallback: any = translations.en;
        for (const k of keys) {
          fallback = fallback?.[k];
        }
        text = typeof fallback === 'string' ? fallback : key;
      }

      // Replace parameters (e.g., {{count}} → 3)
      Object.keys(params).forEach((paramKey) => {
        const regex = new RegExp(`{{${paramKey}}}`, 'g');
        text = text.replace(regex, String(params[paramKey]));
      });

      return text;
    },
    [language]
  );

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    isRTL,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Language Provider Component (wrapper for use outside Router)
 * 
 * Wraps the entire application and provides language state.
 * Automatically sets document direction (LTR/RTL) based on language.
 * 
 * For route-aware language switching, use LanguageProviderInner inside Router.
 */
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Fallback: use default language key if not inside Router
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('smartdry_language');
    return (saved as Language) || 'en';
  });

  const setLanguage = useCallback((newLang: Language) => {
    setLanguageState(newLang);
    localStorage.setItem('smartdry_language', newLang);
  }, []);

  const isRTL = ['ar', 'ur'].includes(language);

  useLayoutEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const t = useCallback(
    (key: TranslationKey, params: { [key: string]: string | number } = {}): string => {
      const keys = key.split('.');
      let text: any = translations[language];
      
      for (const k of keys) {
        text = text?.[k];
      }
      
      if (typeof text !== 'string') {
        let fallback: any = translations.en;
        for (const k of keys) {
          fallback = fallback?.[k];
        }
        text = typeof fallback === 'string' ? fallback : key;
      }

      Object.keys(params).forEach((paramKey) => {
        const regex = new RegExp(`{{${paramKey}}}`, 'g');
        text = text.replace(regex, String(params[paramKey]));
      });

      return text;
    },
    [language]
  );

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    isRTL,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Route-aware Language Provider (must be used inside Router)
 * 
 * Use this component inside Router to get independent language state
 * for POS and Dashboard.
 */
export const LanguageProviderWithRoutes: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <LanguageProviderInner>{children}</LanguageProviderInner>;
};

/**
 * Hook to access translation functions
 * 
 * Usage:
 * ```tsx
 * const { t, language, setLanguage, isRTL } = useTranslation();
 * 
 * <button>{t('pos.sidebar.home')}</button>
 * <div>{t('cart.itemCount', { count: items.length })}</div>
 * ```
 */
export const useTranslation = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

/**
 * Export default for convenience
 */
export default useTranslation;

